import CHOICES from "CHOICES";
import { mutate } from "utils/relay";
import {
  AUTH_TOKEN,
  ENV_MAPPER,
  REFRESH_TOKEN,
  TOKEN_TTL,
} from "utils/constants";
import graphql from "babel-plugin-relay/macro";
import { emptyProxyObject, getEnv, ProxyObjectType } from "utils/utils";
import { Action, Thunk, Computed, thunk, action, computed } from "easy-peasy";
import { Cookies } from "react-cookie";
import moment from "moment";

const mutation = graphql`
  mutation authRefreshMutation($input: RefreshInput!) {
    refreshToken(input: $input) {
      token
      payload
      refreshToken
    }
  }
`;

const cookie = new Cookies();

const UK = CHOICES.UserKind;

const cookieDomain = (env: string) =>
  env === ENV_MAPPER.production
    ? ".v3.nuflights.com"
    : env === ENV_MAPPER.development
      ? `.${env}.nuflights.com`
      : `.jwt.${env}.nuflights.com`;

export interface CurrentUserModel {
  username: string;
  email: string;
}

export interface AuthModel {
  currentUser: CurrentUserModel | ProxyObjectType;
  authToken: string | null;
  isLoggedIn: Computed<AuthModel, boolean>;
  isAdmin: Computed<AuthModel, boolean>;
  setAuthToken: Action<AuthModel, string | null>;
  setCurrentUser: Action<AuthModel, CurrentUserModel | ProxyObjectType>;
  updateAuthToken: Thunk<AuthModel, any>;
  clearAuthToken: Thunk<AuthModel>;
  fetchAuthToken: Thunk<AuthModel, any>;
  initRefreshToken: Thunk<AuthModel, boolean>;
  permissions: any;
  setPermissions: Action<AuthModel, any>;
  updatePermissions: Thunk<AuthModel, any>;
  refreshToken: string | null;
  setRefreshToken: Action<AuthModel, string | null>;
  updateRefreshToken: Thunk<AuthModel, any>;
  clearRefreshToken: Thunk<AuthModel>;
}

const AuthStore: AuthModel = {
  currentUser: emptyProxyObject,
  permissions: {},
  authToken: cookie.get(AUTH_TOKEN),
  refreshToken: cookie.get(REFRESH_TOKEN),

  isLoggedIn: computed((state: any) => state.currentUser !== emptyProxyObject),
  isAdmin: computed((state: any) => state.currentUser.kind === UK.ADMIN),

  setAuthToken: action((state: any, authToken) => {
    state.authToken = authToken;
  }),

  setRefreshToken: action((state: any, refreshToken) => {
    state.refreshToken = refreshToken;
  }),

  setCurrentUser: action((state: any, currentUser) => {
    state.currentUser = currentUser;
  }),

  setPermissions: action((state: any, permissions) => {
    state.permissions = permissions;
  }),

  updatePermissions: thunk((actions, permissions) => {
    let permissionsObj: any = {};
    permissions &&
      permissions
        ?.split(" ")
        ?.map((permission: string) => (permissionsObj[permission] = true));
    actions.setPermissions(permissionsObj);
  }),

  updateAuthToken: thunk((actions, authToken) => {
    getEnv() !== ENV_MAPPER.local
      ? cookie.set(AUTH_TOKEN, authToken.self, {
          domain: cookieDomain(getEnv()),
          path: "/",
          sameSite: "strict",
          secure: true,
          maxAge: Math.floor(authToken?.expiry / 1000),
          // maxAge: authToken?.expiry,
        })
      : cookie.set(AUTH_TOKEN, authToken.self, { path: "/" });
    actions.setAuthToken(authToken.self);
  }),

  updateRefreshToken: thunk((actions, refreshToken) => {
    getEnv() !== ENV_MAPPER.local
      ? cookie.set(REFRESH_TOKEN, refreshToken.self, {
          domain: cookieDomain(getEnv()),
          path: "/",
          sameSite: "strict",
          secure: true,
          maxAge: Math.floor(refreshToken?.expiry / 1000),
          // maxAge: refreshToken?.expiry,
        })
      : cookie.set(REFRESH_TOKEN, refreshToken.self, { path: "/" });
    actions.setRefreshToken(refreshToken.self);
  }),

  clearAuthToken: thunk(async (actions) => {
    window.localStorage.clear();
    getEnv() !== ENV_MAPPER.local
      ? await cookie.remove(AUTH_TOKEN, {
          domain: cookieDomain(getEnv()),
          path: "/",
        })
      : await cookie.remove(AUTH_TOKEN, { path: "/" });
    actions.setAuthToken(null);
    actions.setCurrentUser(emptyProxyObject);
    window.sessionStorage.clear();
  }),

  clearRefreshToken: thunk(async (actions) => {
    window.localStorage.clear();
    getEnv() !== ENV_MAPPER.local
      ? await cookie.remove(REFRESH_TOKEN, {
          domain: cookieDomain(getEnv()),
          path: "/",
        })
      : await cookie.remove(REFRESH_TOKEN, { path: "/" });
    actions.setRefreshToken(null);
    actions.setCurrentUser(emptyProxyObject);
  }),

  fetchAuthToken: thunk(
    (actions, { mutation, input, onSuccess, onFailure }) => {
      mutate({
        mutation,
        input: input,
        onSuccess: function (data: any) {
          let token: any = {
            self: "",
            expiry: new Date(),
          };
          let refreshToken: any = {
            self: "",
            expiry: new Date(),
          };
          let unixTime = Number(moment.unix(data.login.refreshExpiresIn || 0));
          let date = new Date(unixTime);
          if (data.login) {
            token.self = data.login.token;
            token.expiry = date;
            refreshToken.self = data.login.refreshToken;
            refreshToken.expiry = date;
          } else if (data.socialAuth) {
            token = data.socialAuth.token;
          }
          actions.updateAuthToken(token);
          actions.updateRefreshToken(refreshToken);
          onSuccess(data);
        },
        onFailure: function (messages: []) {
          actions.clearAuthToken();
          actions.clearRefreshToken();
          onFailure(messages);
        },
      });
    }
  ),

  initRefreshToken: thunk((actions, initialFetch = true) => {
    // Helper to convert JWT exp -> JS Date correctly
    const getExpiryDate = (exp: number) => {
      const expiryMs = (exp || 0) * 1000; // FIX: convert seconds → milliseconds
      return new Date(expiryMs);
    };

    // First refresh on load
    if (initialFetch && cookie.get(REFRESH_TOKEN)) {
      mutate({
        mutation,
        input: { refreshToken: cookie.get(REFRESH_TOKEN) },
        onSuccess: function (data: any) {
          const exp = data.refreshToken.payload.exp;
          const expiryDate = getExpiryDate(exp);
          let token: any = {
            self: data.refreshToken.token,
            expiry: expiryDate,
          };
          let refreshToken: any = {
            self: data.refreshToken.refreshToken,
            expiry: expiryDate,
          };
          actions.updateAuthToken(token);
          actions.updateRefreshToken(refreshToken);
        },
        onFailure: function (messages: any) {
          actions.clearAuthToken();
          actions.clearRefreshToken();
        },
      });
    }
    // Set up the interval and store its ID
    setInterval(() => {
      if (cookie.get(REFRESH_TOKEN)) {
        mutate({
          mutation,
          input: { refreshToken: cookie.get(REFRESH_TOKEN) },
          onSuccess: function (data: any) {
            const exp = data.refreshToken.payload.exp;
            const expiryDate = getExpiryDate(exp);
            let token: any = {
              self: data.refreshToken.token,
              expiry: expiryDate,
            };
            let refreshToken: any = {
              self: data.refreshToken.refreshToken,
              expiry: expiryDate,
            };
            actions.updateAuthToken(token);
            actions.updateRefreshToken(refreshToken);
          },
          onFailure: function (messages: any) {
            actions.clearAuthToken();
            actions.clearRefreshToken();
          },
        });
      }
    }, TOKEN_TTL);
  }),
};

export default AuthStore;
